import { fatorAnotado } from './moedaFornecedorPolicy';
import { encargosExteriorGrupoBrl, impostoNfAccessGrupoPorEtapa, valoresBrlGrupoPorEtapa } from './colunaVigentePolicy';
import { moedaOrcamentoDeBrl } from './valorBrlPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';
const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    if (typeof valor !== 'string') return 0;

    const texto = valor.trim();
    if (!texto) return 0;
    const normalizado = texto.includes(',')
        ? texto.replace(/\./g, '').replace(',', '.')
        : texto;
    const convertido = Number(normalizado);
    return Number.isFinite(convertido) ? convertido : 0;
};

const pagamento = (valor) => (valor && typeof valor === 'object' ? valor.id : valor);
const pagaPeloCliente = (subitem) => String(pagamento(subitem?.fornecedorpagamento) || '').trim().toLowerCase() === 'cliente';
// Opção (D-0113, item só para visualização) também fica fora do que a Access paga.
const naoPagaPeloCliente = (subitem) => !pagaPeloCliente(subitem) && !ehSomenteVisualizacao(subitem);

/**
 * Recorte informativo do orçamento: o que não é pago pelo cliente é pago pela
 * Access. Linhas legadas sem seleção continuam no total, como no Financeiro.
 * Imposto NF Access automático do grupo (Chiesi/internacional — não é linha): entra por etapa pela
 * fórmula única (colunaVigentePolicy.impostoNfAccessGrupoPorEtapa), só sobre a base que a Access paga
 * (linha do Cliente fora). Grupo AGÊNCIA com 1945 manual: a linha já está na soma; o automático é 0.
 * opcoesNf: { aplicaImposto, clienteEhChiesi, fornecedorAccess, cambio } — sem `aplicaImposto`, sem NF.
 */
export const calcularTotaisAccess = (itens = [], opcoesNf = {}) => {
    const totais = { preEvento: 0, negociado: 0, fechamento: 0 };

    itens.forEach((item) => {
        const nf = impostoNfAccessGrupoPorEtapa(item || {}, { ...opcoesNf, incluirLinha: naoPagaPeloCliente });
        totais.preEvento += nf.preEvento;
        totais.negociado += nf.negociado;
        totais.fechamento += nf.fechamento;
        (item?.subitem || []).forEach((subitem) => {
            if (!naoPagaPeloCliente(subitem)) {
                return;
            }
            // Moeda do fornecedor (27/09): linha de moeda própria vai para a moeda do orçamento pelo fator
            // anotado (moedaFornecedorPolicy.anotarFatoresMoeda); legado × 1.
            totais.preEvento += numero(subitem?.totalPreEvento) * fatorAnotado(subitem, 'pre_evento');
            totais.negociado += numero(subitem?.totalNegociado) * fatorAnotado(subitem, 'negociado');
            // Fechamento exterior concluído: o valor pago de fato (exibição), nunca o gravado.
            totais.fechamento += numero(subitem?.fechamento_exibicao ? subitem.fechamento_exibicao.totalFechamento : subitem?.totalFechamento) * fatorAnotado(subitem, 'fechamento');
        });
    });

    // Valor exato em reais (contrato 02/10, regra 3): R$ = Σ R$ das linhas que a Access paga + NF Access
    // em R$ de cada grupo (valoresBrlGrupoPorEtapa) + taxas do exterior dessas linhas (item 2, pós-QA:
    // imposto de remessa + envios + NF da estimativa; no Fechamento, os encargos reais do fechamento
    // concluído). opcoesNf.orcamento = contexto da remessa (moeda/câmbio). opcoesNf.estrangeiro (orçamento
    // em moeda estrangeira): o valor na moeda passa a ser R$ ÷ câmbio, arredondado uma vez (só exibição;
    // no modo R$ exato, opcoesNf.exato, é também o que se grava). Linha paga pelo Cliente fica fora.
    const cambio = Number.isFinite(opcoesNf.cambio) && opcoesNf.cambio > 0 ? opcoesNf.cambio : 1;
    const exato = !!opcoesNf.exato;
    const estrangeiro = exato || !!opcoesNf.estrangeiro;
    const contextoRemessa = opcoesNf.orcamento ?? { moeda: cambio === 1 ? 'real' : 'dolar', cambio };
    const linhas = { preEvento: 0, negociado: 0, fechamento: 0 };
    const encargos = { preEvento: 0, negociado: 0, fechamento: 0 };
    itens.forEach((item) => {
        const grupo = valoresBrlGrupoPorEtapa(item || {}, { ...opcoesNf, cambio, exato: estrangeiro, incluirLinha: naoPagaPeloCliente, fechamentoExibicao: true });
        const taxas = encargosExteriorGrupoBrl(item || {}, contextoRemessa, { fornecedorAccess: opcoesNf.fornecedorAccess ?? null, incluirLinha: naoPagaPeloCliente });
        Object.keys(linhas).forEach((chave) => {
            linhas[chave] += grupo[chave];
            encargos[chave] += taxas[chave];
        });
    });
    const brl = {};
    // Contrato v2 B: precisão total; arredonda só na exibição (e o valor na moeda, uma vez).
    Object.keys(linhas).forEach((chave) => { brl[chave] = linhas[chave] + encargos[chave]; });
    totais.brl = brl;
    totais.encargosBrl = encargos;
    if (estrangeiro) {
        totais.preEvento = moedaOrcamentoDeBrl(brl.preEvento, cambio);
        totais.negociado = moedaOrcamentoDeBrl(brl.negociado, cambio);
        totais.fechamento = moedaOrcamentoDeBrl(brl.fechamento, cambio);
    } else {
        totais.preEvento += encargos.preEvento;
        totais.negociado += encargos.negociado;
        totais.fechamento += encargos.fechamento;
    }

    return totais;
};
